import {IconButton} from '@wandb/components';
import {type WBMenuOption, WBPopupMenuTrigger} from '@wandb/ui';
import {clsx} from 'clsx';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createSelector} from 'reselect';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Input, Label} from 'semantic-ui-react';

import {useRunSetSummaryQuery} from '../state/graphql/runSetSummaryQuery';
import {useMemoizedSelector} from '../state/hooks';
import * as ViewHooks from '../state/views/hooks';
import * as RunSetActions from '../state/views/runSet/actions';
import type * as RunSetTypes from '../state/views/runSet/types';
import {MultiStateCheckbox} from './MultiStateCheckbox';
import {makeRunSetSelector} from './RunSelector/runSetSelectors';
import {InstrumentedWaveLoader as Loader} from './utility/InstrumentedLoader';

/**
 * Uses createSelector per https://reselect.js.org/api/createSelector
 * Colocated here since it's specific to RunSetDisplay.
 */
const makeRunSetDisplaySelector = (runSetRef: RunSetTypes.Ref) =>
  createSelector(makeRunSetSelector(runSetRef), runSet => ({
    name: runSet.name,
    enabled: runSet.enabled,
  }));

interface RunSetSummaryProps {
  active: boolean;
  className?: string | null;
  duplicateRunSet(): void;
  readOnly?: boolean;
  removeRunSet?(): void;
  runSetRef: RunSetTypes.Ref;
  toggleActive(): void;
}

export const RunSetSummary = React.memo(
  ({
    active,
    className,
    duplicateRunSet,
    readOnly,
    removeRunSet,
    runSetRef,
    toggleActive,
  }: RunSetSummaryProps) => {
    const {runSetSummaryQuery, setEnabled, setName} =
      useRunSetSummary(runSetRef);
    // Use useMemoizedSelector directly with the selector factory instead of the
    // convenience hooks in RunSelectorRunSetContext (e.g., usePinnedAndBaseline).
    // This component is called from the reports path where that provider doesn't exist.
    const partialRunSet = useMemoizedSelector(
      makeRunSetDisplaySelector,
      runSetRef
    );

    const [editNameText, setEditNameText] = useState(partialRunSet.name);
    const [isEditingName, setIsEditingName] = useState(false);
    const nameInputRef = useRef<Input | null>(null);

    const saveName = useCallback(
      (newName: string) => {
        setName(newName);
        setIsEditingName(false);
      },
      [setName]
    );

    useEffect(() => {
      if (isEditingName) {
        nameInputRef.current?.focus();
      }
    }, [isEditingName]);

    const ellipsisMenuItems: WBMenuOption[] = useMemo(() => {
      return [
        ...(!readOnly
          ? [
              {
                icon: 'edit',
                value: 'edit',
                name: 'Rename',
                onSelect: () => {
                  setIsEditingName(true);
                },
              },
              {
                icon: 'copy',
                value: 'copy',
                name: 'Clone',
                onSelect: duplicateRunSet,
              },
            ]
          : []),
        ...(!readOnly && removeRunSet != null
          ? [
              {
                icon: 'delete',
                value: 'delete',
                name: 'Delete',
                onSelect: () => {
                  if (removeRunSet != null) {
                    removeRunSet();
                  }
                },
              },
            ]
          : []),
        {
          icon: 'table',
          value: 'maximize',
          name: active ? 'Minimize' : 'Edit',
          onSelect: () => toggleActive(),
        },
      ];
    }, [active, duplicateRunSet, readOnly, removeRunSet, toggleActive]);

    return (
      <div
        className={clsx('run-set-summary ', active ? 'active' : '', className)}
        onClick={toggleActive}>
        {isEditingName ? (
          <Input
            ref={nameInputRef}
            value={editNameText}
            onClick={(e: React.SyntheticEvent<HTMLInputElement>) =>
              e.stopPropagation()
            }
            onChange={(e: React.SyntheticEvent<HTMLInputElement>) => {
              setEditNameText(e.currentTarget.value);
            }}
            onBlur={(e: React.SyntheticEvent<HTMLInputElement>) => {
              saveName(e.currentTarget.value);
            }}
            onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                saveName(e.currentTarget.value);
              }
            }}
            onMouseDown={(e: React.MouseEvent<HTMLInputElement>) => {
              e.stopPropagation();
            }}
          />
        ) : (
          <React.Fragment>
            <MultiStateCheckbox
              checked={partialRunSet.enabled ? 'checked' : 'unchecked'}
              onClick={() => setEnabled(!partialRunSet.enabled)}
            />
            {partialRunSet.name}
            <Label className="count-label">
              {runSetSummaryQuery.loading ? (
                <Loader name="run-set-summary-loader" size="small" />
              ) : (
                runSetSummaryQuery.data?.project?.selectedCount
              )}
            </Label>
          </React.Fragment>
        )}
        <WBPopupMenuTrigger options={ellipsisMenuItems}>
          {({setOpen, anchorRef}) => {
            return (
              <IconButton
                aria-label="Run Set Options"
                icon="overflow-horizontal"
                variant="ghost"
                ref={anchorRef}
                onPress={() => {
                  setOpen(o => !o);
                }}
              />
            );
          }}
        </WBPopupMenuTrigger>
      </div>
    );
  }
);

function useRunSetSummary(runSetRef: RunSetTypes.Ref) {
  const runSetSummaryQuery = useRunSetSummaryQuery(runSetRef);
  const runSetUpdate = ViewHooks.useViewAction(runSetRef, RunSetActions.update);

  const setEnabled = useCallback(
    (enabled: boolean) => runSetUpdate({enabled}),
    [runSetUpdate]
  );

  const setName = useCallback(
    (name: string) => runSetUpdate({name}),
    [runSetUpdate]
  );

  return {
    runSetSummaryQuery,
    setEnabled,
    setName,
  };
}
